import { RSAA } from 'redux-api-middleware';
import customFailure from 'helpers/customFailureAction';

import { Dispatch } from 'redux';
import { TDispatchResult, TGetRootState } from 'types/shared';

export interface IGeneralSettings {
  defaultCustomers: {
    streamingCustomers: string[];
    downloadCustomers: string[];
  };
  defaultCharts: {
    album: {
      daily: number[];
      weekly: number[];
      currentPositionKPIs: number[];
    };
    track: {
      daily: number[];
      weekly: number[];
      currentPositionKPIs: number[];
    };
  };
  defaultSocialMetrics: number[];
  customerColors: {
    downloadCustomers: { [key: string]: string };
    streamingCustomers: { [key: string]: string };
    streamingListenersCustomers: { [key: string]: string };
  };
}

const REQUEST = 'opus/generalSettings/REQUEST';
const SUCCESS = 'opus/generalSettings/SUCCESS';
const FAILURE = 'opus/generalSettings/FAILURE';

type GeneralSettingsActionTypes =
  | typeof SUCCESS
  | typeof REQUEST
  | typeof FAILURE;

interface IGeneralSettingsAction {
  type?: GeneralSettingsActionTypes;
  payload?: any;
}

const endpoint = '/kv/general_settings';

export interface IGeneralSettingsState {
  loading: boolean;
  success: boolean;
  error: Error | null;
  errorInfo: any;
  data: IGeneralSettings;
}

export const initialState: IGeneralSettingsState = {
  loading: false,
  success: false,
  error: null,
  errorInfo: null,
  data: {} as IGeneralSettings,
};

export default function reducer(
  state: IGeneralSettingsState = initialState,
  action: IGeneralSettingsAction
): IGeneralSettingsState {
  switch (action.type) {
    case REQUEST:
      return {
        ...state,
        loading: true,
      };
    case SUCCESS:
      return {
        ...state,
        loading: false,
        success: true,
        error: null,
        errorInfo: null,
        data: {
          ...state.data,
          ...action.payload,
        },
      };
    case FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: new Error(action.payload.error),
        errorInfo: action.payload.errorInfo,
      };

    default:
      return state;
  }
}

/**
 * Load general settings (defaults) from the '/kv/general_settings' endpoint.
 */
export function loadGeneralSettings(): any {
  return {
    [RSAA]: {
      types: [
        REQUEST,
        SUCCESS,
        {
          type: FAILURE,
          payload: async (
            action: IGeneralSettingsAction,
            state: IGeneralSettingsState,
            res: any
          ): Promise<any> => await customFailure(res),
        },
      ],
      endpoint,
      method: 'GET',
      credentials: 'same-origin',
    },
  };
}

export function shouldLoadGeneralSettings(): TDispatchResult<boolean> {
  return (_: Dispatch, getState: TGetRootState): boolean => {
    const {
      generalSettings: { loading, success },
    } = getState();

    // If the data didn't load before AND the data isn't loading currently, we
    // should probably try to load the data.
    return !success && !loading;
  };
}
